import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import FormField, { PosInput } from '../components/pos/FormField';
import SqlMonacoEditor from '../components/SqlMonacoEditor';
import { validateAdminSql, createAdminExportTemplate, listAdminExportTemplates } from '../services/integrationApi';
import { useTranslation } from 'react-i18next';

const DEFAULT_SQL = `SELECT r.Id, r.ReceiptNumber, r.PaidAt, r.Total
FROM PosReceipts r
WHERE r.SchoolId = {SchoolId}
  AND r.Status = 'paid'
  AND r.PaidAt >= {DateFrom}
  AND r.PaidAt <= {DateTo}`;

export default function AdminExportTemplatesPage() {
    const { t } = useTranslation(['admin', 'common']);
    const role = localStorage.getItem('role');
    const [templates, setTemplates] = useState([]);
    const [form, setForm] = useState({
        name: '', description: '', format: 'xml', queryTemplate: DEFAULT_SQL, xsltTemplate: '', allSchools: true
    });
    const [validation, setValidation] = useState(null);
    const [msg, setMsg] = useState('');

    useEffect(() => { if (role === 'Admin') load(); }, [role]);

    async function load() {
        const res = await listAdminExportTemplates();
        setTemplates((res.data || []).filter(tpl => !tpl.isSystem || tpl.templateType === 'custom_sql'));
    }

    async function validate() {
        const res = await validateAdminSql(form.queryTemplate);
        setValidation(res.data);
    }

    async function save() {
        const v = await validateAdminSql(form.queryTemplate);
        if (!v.data?.isValid) {
            setValidation(v.data);
            return;
        }
        await createAdminExportTemplate({
            name: form.name,
            description: form.description,
            templateType: 'custom_sql',
            format: form.format,
            queryTemplate: form.queryTemplate,
            xsltTemplate: form.xsltTemplate || null,
            allSchools: form.allSchools
        });
        setMsg(t('admin:exportTemplates.created'));
        load();
    }

    if (role !== 'Admin') {
        return (
            <OwnerPageLayout title={t('admin:exportTemplates.title')} className="pos-page pos-ui" maxWidth={900}>
                <p>{t('admin:exportTemplates.adminOnly')}</p>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('admin:exportTemplates.title')}
            subtitle={t('admin:exportTemplates.subtitle')}
            className="pos-page pos-ui"
            maxWidth={900}
            actions={<Link to="/admin/references" className="pos-btn sp-btn--ghost">{t('admin:exportTemplates.backRefs')}</Link>}
        >
                {msg && <p style={{ color: '#15803d' }}>{msg}</p>}

                <div className="pos-card" style={{ marginBottom: 24 }}>
                    <h3>{t('admin:exportTemplates.newTemplate')}</h3>
                    <FormField label={t('admin:exportTemplates.name')}><PosInput value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormField>
                    <FormField label={t('admin:exportTemplates.sqlQuery')}>
                        <SqlMonacoEditor
                            height={300}
                            value={form.queryTemplate}
                            onChange={queryTemplate => setForm({ ...form, queryTemplate })}
                        />
                    </FormField>
                    <p style={{ fontSize: 12, color: 'var(--pos-muted)' }}>{t('admin:exportTemplates.paramsHint')}</p>
                    <button type="button" className="pos-btn pos-btn--ghost" onClick={validate}>{t('admin:exportTemplates.validateSql')}</button>
                    {validation && (
                        <div style={{ marginTop: 8, fontSize: 14 }}>
                            {validation.isValid ? <span style={{ color: '#15803d' }}>{t('admin:exportTemplates.sqlValid')}</span> : null}
                            {validation.errors?.map((e, i) => <div key={i} className="cashier-error">{e}</div>)}
                            {validation.warnings?.map((w, i) => <div key={i} style={{ color: '#b45309' }}>{w}</div>)}
                        </div>
                    )}
                    <FormField label={t('admin:exportTemplates.xsltOptional')}>
                        <textarea className="pos-input" style={{ minHeight: 100, fontFamily: 'monospace', fontSize: 12 }}
                            value={form.xsltTemplate} onChange={e => setForm({ ...form, xsltTemplate: e.target.value })} />
                    </FormField>
                    <button type="button" className="pos-btn pos-btn--primary" onClick={save}>{t('admin:exportTemplates.saveTemplate')}</button>
                </div>

                <h3>{t('admin:exportTemplates.existing')}</h3>
                <ul>{templates.map(tpl => <li key={tpl.id}>{tpl.name} ({tpl.templateType})</li>)}</ul>
        </OwnerPageLayout>
    );
}
